import { Fragment, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  CheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  XMarkIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { useNotifications, Notification } from '../hooks/useNotifications';
import clsx from 'clsx';

// Category icons
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  affiliate: UserPlusIcon,
  payment: CurrencyDollarIcon,
  conversion: ArrowPathIcon,
  fraud: ShieldExclamationIcon,
  campaign: MegaphoneIcon,
  system: Cog6ToothIcon,
};

// Priority styles
const priorityStyles: Record<string, { bg: string; border: string; icon: string }> = {
  high: {
    bg: 'bg-error-500/10',
    border: 'border-error-500/30',
    icon: 'text-error-400',
  },
  medium: {
    bg: 'bg-warning-500/10',
    border: 'border-warning-500/30',
    icon: 'text-warning-400',
  },
  low: {
    bg: 'bg-primary-500/10',
    border: 'border-primary-500/30',
    icon: 'text-primary-400',
  },
};

// Format time ago
function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onClick?: () => void;
}

function NotificationItem({ notification, onMarkAsRead, onClick }: NotificationItemProps) {
  const CategoryIcon = categoryIcons[notification.category] || InformationCircleIcon;
  const styles = priorityStyles[notification.priority] || priorityStyles.medium;

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    onClick?.();
  };

  const content = (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={clsx(
        'flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200',
        notification.isRead
          ? 'bg-transparent hover:bg-[var(--bg-card)]'
          : `${styles.bg} hover:opacity-90`,
        !notification.isRead && `border ${styles.border}`
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div
        className={clsx(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          notification.isRead ? 'bg-[var(--bg-card)]' : styles.bg
        )}
      >
        <CategoryIcon
          className={clsx('h-4 w-4', notification.isRead ? 'text-[var(--text-muted)]' : styles.icon)}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={clsx(
              'text-sm font-medium truncate',
              notification.isRead ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'
            )}
          >
            {notification.title}
          </p>
          {notification.priority === 'high' && !notification.isRead && (
            <ExclamationTriangleIcon className="h-4 w-4 text-error-400 flex-shrink-0" />
          )}
        </div>
        <p
          className={clsx(
            'text-xs mt-0.5 line-clamp-2',
            notification.isRead ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'
          )}
        >
          {notification.message}
        </p>
        <p className="text-xs text-[var(--text-muted)] mt-1">{timeAgo(notification.createdAt)}</p>
      </div>

      {/* Mark as read button */}
      {!notification.isRead && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkAsRead(notification.id);
          }}
          className="flex-shrink-0 p-1 rounded-full hover:bg-[var(--bg-card)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          title="Mark as read"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
      )}
    </motion.div>
  );

  // If there's an action URL, wrap in Link
  if (notification.actionUrl) {
    return (
      <Link to={notification.actionUrl} className="block">
        {content}
      </Link>
    );
  }

  return content;
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    notifications,
    unreadCount,
    isConnected,
    soundEnabled,
    toggleSound,
    markAsRead,
    markAllAsRead,
    requestPermission,
  } = useNotifications();

  // Request notification permission on mount
  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const recentNotifications = notifications.slice(0, 10);

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'relative p-2 rounded-xl transition-all duration-200',
          isOpen
            ? 'bg-primary-500/20 text-primary-400'
            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]'
        )}
        title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        {unreadCount > 0 ? (
          <BellIconSolid className="h-6 w-6" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}

        {/* Unread badge */}
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-error-500 rounded-full shadow-lg"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}

        {/* Connection indicator */}
        <span
          className={clsx(
            'absolute bottom-0 right-0 w-2 h-2 rounded-full ring-2 ring-[var(--bg-secondary)]',
            isConnected ? 'bg-success-500' : 'bg-error-500'
          )}
          title={isConnected ? 'Connected' : 'Disconnected'}
        />
      </button>

      {/* Dropdown */}
      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-96 origin-top-right rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] shadow-2xl shadow-black/20 z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium text-primary-400 bg-primary-500/20 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Sound toggle */}
              <button
                onClick={toggleSound}
                className={clsx(
                  'p-1.5 rounded-lg transition-colors',
                  soundEnabled
                    ? 'text-primary-400 hover:bg-primary-500/20'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-card)]'
                )}
                title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
              >
                {soundEnabled ? (
                  <SpeakerWaveIcon className="h-4 w-4" />
                ) : (
                  <SpeakerXMarkIcon className="h-4 w-4" />
                )}
              </button>

              {/* Mark all as read */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors"
                  title="Mark all as read"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                </button>
              )}

              {/* Close */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {recentNotifications.length > 0 ? (
              <div className="p-2 space-y-1">
                <AnimatePresence>
                  {recentNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkAsRead={markAsRead}
                      onClick={() => setIsOpen(false)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="p-8 text-center">
                <BellIcon className="h-12 w-12 mx-auto text-[var(--text-muted)] opacity-50" />
                <p className="mt-3 text-sm text-[var(--text-secondary)]">No notifications yet</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  We'll notify you when something important happens
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {recentNotifications.length > 0 && (
            <div className="border-t border-[var(--border-color)] p-2">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="block w-full px-3 py-2 text-sm text-center text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
}
